import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  AlertTriangle, XCircle, Users, Eye, CheckCircle, 
  ArrowLeft, Shield, Smartphone, CreditCard, UserX,
  ArrowRight
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FraudStatisticsPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const t = {
    fr: {
      pageTitle: 'La fraude dans la revente de billets',
      pageSubtitle: 'Comprendre les risques pour mieux se protéger',
      backButton: 'Retour',
      statsTitle: 'Les chiffres alarmants',
      statsSubtitle: 'Depuis plus de 15 ans, les réseaux sociaux sont devenus le principal lieu de revente de billets entre particuliers. Sans intermédiaire de confiance, ces transactions exposent acheteurs et vendeurs à des risques majeurs.',
      stat1: '15+',
      stat1Label: 'années de fraudes documentées',
      stat2: '85%',
      stat2Label: 'des fraudes sur réseaux sociaux',
      stat3: '100%',
      stat3Label: 'des fraudes suivent les mêmes patterns',
      scamTypesTitle: 'Types d\'arnaques les plus fréquents',
      scam1Title: 'Billet jamais livré',
      scam1Desc: 'L\'acheteur envoie l\'argent, le vendeur disparaît sans jamais envoyer le billet.',
      scam1Freq: '40% des cas',
      scam2Title: 'Faux screenshots de paiement',
      scam2Desc: 'Capture d\'écran truquée d\'un virement Interac ou d\'un transfert inexistant.',
      scam2Freq: '25% des cas',
      scam3Title: 'Faux billets PDF',
      scam3Desc: 'Billets falsifiés ou déjà utilisés, découverts à l\'entrée de l\'événement.',
      scam3Freq: '20% des cas',
      scam4Title: 'Comptes piratés',
      scam4Desc: 'Fraude via un compte Facebook/Instagram piraté appartenant à une vraie personne.',
      scam4Freq: '15% des cas',
      commonPointTitle: 'Point commun de toutes ces fraudes',
      commonPointDesc: 'Les parties doivent se faire confiance aveuglément, sans aucune garantie ni protection.',
      protectionTitle: 'Comment Middleman vous protège',
      protection1: 'Les billets sont validés AVANT que l\'acheteur ne paie',
      protection2: 'Les screenshots sont inutiles - seuls les vrais billets comptent',
      protection3: 'Aucun échange de coordonnées personnelles entre acheteur et vendeur',
      protection4: 'Timeline transparente et traçable de chaque transaction',
      protection5: 'Service de médiation en cas de litige',
      principleTitle: 'Notre principe fondamental',
      principleDesc: 'Les billets d\'abord. L\'argent ensuite.',
      ctaTitle: 'Protégez-vous dès maintenant',
      ctaDesc: 'Ne prenez plus de risques inutiles. Utilisez Middleman pour sécuriser toutes vos transactions de billets.',
      ctaButton: 'Commencer un transfert sécurisé',
      faqButton: 'Consulter la FAQ'
    },
    en: {
      pageTitle: 'Fraud in Ticket Resale',
      pageSubtitle: 'Understanding the risks to better protect yourself',
      backButton: 'Back',
      statsTitle: 'Alarming Statistics',
      statsSubtitle: 'For over 15 years, social media has become the main place for ticket resale between individuals. Without a trusted intermediary, these transactions expose buyers and sellers to major risks.',
      stat1: '15+',
      stat1Label: 'years of documented fraud',
      stat2: '85%',
      stat2Label: 'of fraud on social media',
      stat3: '100%',
      stat3Label: 'of frauds follow the same patterns',
      scamTypesTitle: 'Most Common Types of Scams',
      scam1Title: 'Ticket never delivered',
      scam1Desc: 'The buyer sends the money, the seller disappears without ever sending the ticket.',
      scam1Freq: '40% of cases',
      scam2Title: 'Fake payment screenshots',
      scam2Desc: 'Forged screenshot of an Interac transfer or non-existent payment.',
      scam2Freq: '25% of cases',
      scam3Title: 'Fake PDF tickets',
      scam3Desc: 'Falsified or already used tickets, discovered at the event entrance.',
      scam3Freq: '20% of cases',
      scam4Title: 'Hacked accounts',
      scam4Desc: 'Fraud via a hacked Facebook/Instagram account belonging to a real person.',
      scam4Freq: '15% of cases',
      commonPointTitle: 'Common point of all these frauds',
      commonPointDesc: 'The parties must blindly trust each other, without any guarantee or protection.',
      protectionTitle: 'How Middleman Protects You',
      protection1: 'Tickets are validated BEFORE the buyer pays',
      protection2: 'Screenshots are useless - only real tickets matter',
      protection3: 'No exchange of personal information between buyer and seller',
      protection4: 'Transparent and traceable timeline for each transaction',
      protection5: 'Mediation service in case of dispute',
      principleTitle: 'Our Fundamental Principle',
      principleDesc: 'Tickets first. Money after.',
      ctaTitle: 'Protect Yourself Now',
      ctaDesc: 'Don\'t take unnecessary risks anymore. Use Middleman to secure all your ticket transactions.',
      ctaButton: 'Start a Secure Transfer',
      faqButton: 'View FAQ'
    }
  };

  const text = t[language] || t.fr;

  const stats = [
    { icon: Shield, stat: text.stat1, label: text.stat1Label, color: 'text-brand-600', bgColor: 'bg-brand-100' },
    { icon: Users, stat: text.stat2, label: text.stat2Label, color: 'text-brand-600', bgColor: 'bg-brand-100' },
    { icon: Eye, stat: text.stat3, label: text.stat3Label, color: 'text-brand-600', bgColor: 'bg-brand-100' },
  ];

  const scams = [
    { icon: XCircle, title: text.scam1Title, desc: text.scam1Desc, freq: text.scam1Freq },
    { icon: Smartphone, title: text.scam2Title, desc: text.scam2Desc, freq: text.scam2Freq },
    { icon: CreditCard, title: text.scam3Title, desc: text.scam3Desc, freq: text.scam3Freq },
    { icon: UserX, title: text.scam4Title, desc: text.scam4Desc, freq: text.scam4Freq },
  ];

  const protections = [
    text.protection1,
    text.protection2,
    text.protection3,
    text.protection4,
    text.protection5,
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      
      {/* Spacer for fixed navbar */}
      <div className="h-16 md:h-20"></div>

      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-12 md:py-20">
        <div className="container mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-6 text-gray-300 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {text.backButton}
          </Button>
          
          <div className="text-center max-w-3xl mx-auto">
            <Shield className="h-16 w-16 text-brand-400 mx-auto mb-6" />
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-4">{text.pageTitle}</h1>
            <p className="text-base md:text-xl text-gray-300">
              {text.pageSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-center mb-4">{text.statsTitle}</h2>
          <p className="text-sm md:text-base text-gray-600 text-center max-w-3xl mx-auto mb-8 md:mb-12">
            {text.statsSubtitle}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl mx-auto">
            {stats.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card key={idx} className="text-center border-2 border-brand-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 md:p-8">
                    <div className={`w-16 h-16 ${item.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <Icon className={`h-8 w-8 ${item.color}`} />
                    </div>
                    <p className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{item.stat}</p>
                    <p className="text-sm md:text-base text-gray-600">{item.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scam Types Section */}
      <div className="py-12 md:py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-center mb-8 md:mb-12">{text.scamTypesTitle}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
            {scams.map((scam, idx) => {
              const Icon = scam.icon;
              return (
                <Card key={idx} className="border-l-4 border-gray-800 hover:shadow-lg transition-shadow">
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-gray-700" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold text-gray-900 text-sm md:text-base">{scam.title}</h3>
                        </div>
                        <p className="text-sm text-gray-600">{scam.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Common Point */}
          <div className="mt-8 md:mt-12 max-w-3xl mx-auto">
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 md:p-6 text-center">
              <Shield className="h-8 w-8 text-brand-600 mx-auto mb-3" />
              <h3 className="font-bold text-gray-800 mb-2 text-sm md:text-base">{text.commonPointTitle}</h3>
              <p className="text-gray-700 text-sm md:text-base">{text.commonPointDesc}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Protection Section */}
      <div className="py-12 md:py-16 bg-brand-600 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <Shield className="h-12 w-12 text-brand-200 mx-auto mb-4" />
            <h2 className="text-xl md:text-3xl font-bold">{text.protectionTitle}</h2>
          </div>
          
          <div className="max-w-2xl mx-auto space-y-3 md:space-y-4">
            {protections.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white/10 rounded-lg p-3 md:p-4">
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-brand-200 flex-shrink-0 mt-0.5" />
                <p className="text-sm md:text-base text-brand-50">{item}</p>
              </div>
            ))}
          </div>

          {/* Principle */}
          <div className="mt-8 md:mt-12 max-w-xl mx-auto text-center">
            <div className="bg-white/20 rounded-xl p-6 md:p-8">
              <p className="text-brand-100 mb-2 text-sm md:text-base">{text.principleTitle}</p>
              <p className="text-xl md:text-2xl font-bold">{text.principleDesc}</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-4">{text.ctaTitle}</h2>
          <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto mb-8">
            {text.ctaDesc}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/auth')}
              className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-3"
            >
              {text.ctaButton}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/faq')}
              className="border-brand-600 text-brand-600 hover:bg-brand-50 px-6 py-3"
            >
              {text.faqButton}
            </Button>
          </div>
        </div>
      </div>

      <Footer variant="full" />
    </div>
  );
};

export default FraudStatisticsPage;
